import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type {
  ActivityMessage,
  ChatAppRequest,
  ChatAppRequestOverrides,
  ServerMessage,
} from "@/api/models"

import { explorerChatWebSocketApi } from "@/api/api"
import { VectorFieldOptions } from "@/api/models"
import { safeImport } from "@/lib/chunk-reload"
import { SettingsService } from "@/services/settings-service"

import { useWorkflowStore } from "../stores/workflow-store"
import { toWorkflowTestRunActivity } from "../utils/workflow-test-run-activity"
import {
  createWorkflowTestRunTimeout,
  WORKFLOW_TEST_RUN_TIMEOUT_MESSAGE,
} from "../utils/workflow-test-run-timeout"

/**
 * `overrides.user_input_data` as the backend reads it: START-node text fields
 * keyed by field key (`text_0: "..."`), plus one `{text}` envelope per mid-flow
 * User Input node keyed by its `inputId` (#2964, see
 * `utils/workflow-test-run-user-inputs.ts`).
 */
export type TestRunUserInputData = Record<string, string | { text: string }>

type UseWorkflowTestRunOptions = {
  workflowId: string | undefined
  onComplete?: () => void
  onError?: (error: string) => void
}

type UseWorkflowTestRunReturn = {
  isRunning: boolean
  output: string
  error: string | null
  activities: Array<{ nodeId: string; message: string; timestamp: number }>
  /**
   * Run the workflow under test.
   *
   * @param input Legacy free-form text used as the first chat message (and the
   *   back-compat `{input}` value). Kept for single-field / no-field flows.
   * @param files Files to upload through the existing file pipeline.
   * @param userInputData Per-field TEXT values keyed by each start field's key
   *   (e.g. `text_0`), plus `{text}` answers keyed by inputId for mid-flow User
   *   Input nodes (#2964). Sent identically to production via
   *   `context.overrides.user_input_data` so `{text_0}` etc. resolve downstream.
   *   File fields are NEVER placed here — they ride the file-upload pipeline.
   */
  runWorkflow: (
    input?: string,
    files?: File[],
    userInputData?: TestRunUserInputData
  ) => Promise<void>
  stopWorkflow: () => void
  clearResults: () => void
}

export function useWorkflowTestRun({
  workflowId,
  onComplete,
  onError,
}: UseWorkflowTestRunOptions): UseWorkflowTestRunReturn {
  const { t } = useTranslation(undefined, { keyPrefix: "workflowLanding" })
  const [isRunning, setIsRunning] = useState(false)
  const [output, setOutput] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [activities, setActivities] = useState<
    Array<{ nodeId: string; message: string; timestamp: number }>
  >([])
  const abortControllerRef = useRef<AbortController | null>(null)
  const runTimeoutRef = useRef<ReturnType<typeof createWorkflowTestRunTimeout> | null>(null)
  const {
    startExecution,
    setExecutingNode,
    markNodeCompleted,
    appendExecutionOutput,
    setExecutionError,
    addExecutionActivity,
    stopExecution,
    clearExecution,
    setNodeOutput,
    setLoopProgress,
  } = useWorkflowStore()
  const nodes = useWorkflowStore((state) => state.nodes)
  const edges = useWorkflowStore((state) => state.edges)

  const endNodeIds = useMemo(
    () => nodes.filter((node) => node.type === "end").map((node) => node.id),
    [nodes]
  )
  const terminalNodeIds = useMemo(() => {
    if (endNodeIds.length === 0) return []
    const endNodeSet = new Set(endNodeIds)
    return edges.filter((edge) => endNodeSet.has(edge.target)).map((edge) => edge.source)
  }, [edges, endNodeIds])
  const hasExplicitOutputNodes = useMemo(
    () =>
      nodes.some((node) => {
        const data = node.data as Record<string, unknown> | undefined
        return data?.showOutput === true
      }),
    [nodes]
  )

  const resolveEndNodeOutput = useCallback(
    (accumulatedOutput: string) => {
      const trimmed = accumulatedOutput.trim()
      if (trimmed.length > 0) return accumulatedOutput
      // Get latest nodeOutputs from store to avoid stale closure
      const currentNodeOutputs = useWorkflowStore.getState().nodeOutputs
      for (const nodeId of terminalNodeIds) {
        const output = currentNodeOutputs[nodeId]
        if (typeof output === "string" && output.trim().length > 0) {
          return output
        }
      }
      return ""
    },
    [terminalNodeIds]
  )

  const handleMessage = useCallback(
    (message: ServerMessage) => {
      if (message.type === "activity") {
        // Node activity is proof the run is alive and making progress — reset
        // the inactivity timeout so a long-running loop isn't aborted mid-flight.
        runTimeoutRef.current?.bump()
        const activityMsg = message as ActivityMessage

        const getNodeOutputPayloadString = (details: Record<string, unknown> | undefined) => {
          const nodeIo = details?.node_io
          if (nodeIo !== undefined) {
            try {
              return typeof nodeIo === "string" ? nodeIo : JSON.stringify(nodeIo)
            } catch {
              // Fall back to legacy node_output if node_io can't be stringified
            }
          }

          const legacy = details?.node_output
          return typeof legacy === "string" ? legacy : undefined
        }

        // Helper to parse loop progress from details
        const getLoopProgress = (details: Record<string, unknown> | undefined) => {
          const progress = details?.loop_progress
          if (
            progress &&
            typeof progress === "object" &&
            "current" in progress &&
            "total" in progress
          ) {
            const p = progress as { current: unknown; total: unknown }
            if (typeof p.current === "number" && typeof p.total === "number") {
              return { current: p.current, total: p.total }
            }
          }
          return undefined
        }

        // Handle workflow node activity
        if (activityMsg.activity_type === "workflow_node") {
          const nodeId = activityMsg.details?.node_id as string | undefined
          const nodeOutput = getNodeOutputPayloadString(activityMsg.details)

          if (nodeId) {
            setExecutingNode(nodeId)
            addExecutionActivity({
              nodeId,
              message: activityMsg.message,
              timestamp: activityMsg.timestamp,
            })
            setActivities((prev) => [
              ...prev,
              {
                nodeId,
                message: activityMsg.message,
                timestamp: activityMsg.timestamp,
              },
            ])

            if (nodeOutput !== undefined) {
              setNodeOutput(nodeId, nodeOutput)
            }
          }
        } else if (activityMsg.activity_type === "node_completed") {
          const nodeId = activityMsg.details?.node_id as string | undefined
          const nodeOutput = getNodeOutputPayloadString(activityMsg.details)
          const loopProgress = getLoopProgress(activityMsg.details)

          if (nodeId && loopProgress) {
            setLoopProgress(nodeId, loopProgress.current, loopProgress.total)
            setExecutingNode(nodeId)
            return
          }

          if (nodeId) {
            markNodeCompleted(nodeId)
          }

          if (nodeId && nodeOutput !== undefined) {
            setNodeOutput(nodeId, nodeOutput)
          }
        } else {
          const diagnosticActivity = toWorkflowTestRunActivity(activityMsg, (key, defaultValue) =>
            t(key, defaultValue)
          )
          if (diagnosticActivity) {
            addExecutionActivity(diagnosticActivity)
            setActivities((previous) => [...previous, diagnosticActivity])
          }
        }
      }
    },
    [setExecutingNode, markNodeCompleted, addExecutionActivity, setNodeOutput, setLoopProgress, t]
  )

  const runWorkflow = useCallback(
    async (input?: string, files?: File[], userInputData?: TestRunUserInputData) => {
      if (!workflowId) {
        setError("No workflow ID provided")
        onError?.("No workflow ID provided")
        return
      }

      // Reset state
      setIsRunning(true)
      setOutput("")
      setError(null)
      setActivities([])
      startExecution()

      // Create abort controller
      const abortController = new AbortController()
      abortControllerRef.current = abortController
      const runTimeout = createWorkflowTestRunTimeout(abortController)
      runTimeoutRef.current = runTimeout

      const settingsService = SettingsService.getInstance()

      // Get available libraries for the "flow" module to enable library_search tool
      const searchServices = settingsService.settings.indexes
        .filter((index) => {
          // Include libraries unless "flow" is in blocked_modules
          if (!index.blocked_modules || index.blocked_modules.length === 0) {
            return true
          }
          return !index.blocked_modules.includes("flow")
        })
        .map((index) => index.id)

      // Only the TEXT (string) fields belong in user_input_data — file fields ride
      // the file-upload pipeline (see `files` above). Drop empty maps so legacy
      // single-field / no-field flows send exactly what they did before.
      const hasUserInputData = !!userInputData && Object.keys(userInputData).length > 0

      // `user_input_data` is read by the backend from `overrides` (see
      // approaches/workflows/contracts/workflows.py) but isn't yet declared on the
      // shared ChatAppRequestOverrides type, so attach it via an extended local.
      const overrides: ChatAppRequestOverrides & { user_input_data?: TestRunUserInputData } = {
        workflow_id: workflowId,
        use_custom_gpt: true,
        custom_gpt_model: "auto",
        vector_fields: [VectorFieldOptions.Embedding],
        search_services: searchServices,
        ...(hasUserInputData ? { user_input_data: userInputData } : {}),
      }

      // Build the chat request
      const request: ChatAppRequest = {
        messages: [{ content: input || "Execute workflow", role: "user" }],
        stream: true,
        context: {
          overrides,
        },
        session_state: null,
      }

      try {
        const response = await Promise.race([
          explorerChatWebSocketApi(
            request,
            files && files.length > 0 ? files : undefined,
            settingsService.token,
            abortController.signal,
            handleMessage,
            "open"
          ),
          runTimeout.promise,
        ])

        const readStream = async () => {
          let accumulatedOutput = ""
          if (response.body) {
            const { default: readNDJSONStream } = await safeImport(
              () => import("ndjson-readablestream")
            )

            for await (const event of readNDJSONStream(response.body)) {
              if (!event) continue

              if (abortController.signal.aborted) {
                const abortError = new Error("AbortError")
                abortError.name = "AbortError"
                throw abortError
              }

              if (event?.error || event?.type === "error") {
                const errorMessage = String(event?.message || event?.error || "Unknown error")
                throw new Error(errorMessage)
              }

              if (typeof event.content === "string" && event.content.length > 0) {
                // Streamed content is also proof of life — reset the inactivity
                // timeout on every chunk, same as node activity.
                runTimeout.bump()
                accumulatedOutput += event.content
                setOutput(accumulatedOutput)
                appendExecutionOutput(event.content)
              }
            }
          }
          return accumulatedOutput
        }

        // Read streaming response using NDJSON parser
        const accumulatedOutput = await Promise.race([readStream(), runTimeout.promise] as const)

        const endNodeOutput = resolveEndNodeOutput(accumulatedOutput)
        if (!hasExplicitOutputNodes && endNodeOutput && endNodeIds.length > 0) {
          endNodeIds.forEach((endNodeId) => {
            setNodeOutput(endNodeId, endNodeOutput)
          })
        }

        // Workflow completed successfully
        stopExecution()
        setIsRunning(false)
        onComplete?.()
      } catch (err) {
        if (abortController.signal.aborted && !runTimeout.timedOut()) {
          // User cancelled
          stopExecution()
          setIsRunning(false)
          return
        }

        let errorMessage = "Workflow execution failed"
        if (runTimeout.timedOut()) {
          errorMessage = WORKFLOW_TEST_RUN_TIMEOUT_MESSAGE
        } else if (err instanceof Error) {
          errorMessage = err.message
        }
        setError(errorMessage)
        stopExecution()
        setExecutionError(errorMessage)
        setIsRunning(false)
        onError?.(errorMessage)
      } finally {
        runTimeout.clear()
        if (runTimeoutRef.current === runTimeout) {
          runTimeoutRef.current = null
        }
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null
        }
      }
    },
    [
      workflowId,
      startExecution,
      appendExecutionOutput,
      stopExecution,
      setExecutionError,
      handleMessage,
      resolveEndNodeOutput,
      hasExplicitOutputNodes,
      endNodeIds,
      setNodeOutput,
      onComplete,
      onError,
    ]
  )

  const stopWorkflow = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    stopExecution()
    setIsRunning(false)
  }, [stopExecution])

  const clearResults = useCallback(() => {
    setOutput("")
    setError(null)
    setActivities([])
    clearExecution()
  }, [clearExecution])

  // Abort any in-flight test run when the edited workflow changes or the hook
  // unmounts. Without this, a run started for workflow A keeps streaming its
  // handleMessage updates (output/activities/node outputs) into the shared
  // execution store after the user switches to workflow B — contaminating B's
  // test panel with A's results. The page's id-change effect clears the store
  // once; aborting here stops the stream that would otherwise re-populate it.
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
        abortControllerRef.current = null
      }
    }
  }, [workflowId])

  return {
    isRunning,
    output,
    error,
    activities,
    runWorkflow,
    stopWorkflow,
    clearResults,
  }
}
